import React, { useEffect, useState, useCallback } from 'react';
import { GitBranch, RefreshCw, Clock, AlertCircle, Circle } from 'lucide-react';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';
const apiFetch = (url: string, opts: RequestInit = {}) =>
  fetch(url, { credentials: 'include', ...opts });
const POLL_MS = 15_000;

interface PlaceNode {
  id: string;
  label: string;
  token_count: number;
  active: boolean;
  token_state: string;
}

interface TransitionEdge {
  id: string;
  name: string;
  skill: string;
  from_places: string[];
  to_places: string[];
}

interface WorkflowData {
  id: string;
  name: string;
  places: PlaceNode[];
  transitions: TransitionEdge[];
  total_tokens: number;
  last_activity_seconds: number | null;
  updated_at: string;
}

function formatAge(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
}

function PlaceChip({ place }: { place: PlaceNode }) {
  const active = place.active;
  const waiting = place.token_state === 'waiting';
  return (
    <div
      className="flex flex-col items-center gap-1 min-w-0"
      title={place.id}
    >
      <div
        className="relative flex items-center justify-center rounded-lg px-3 py-2 text-xs font-medium transition-colors"
        style={{
          minWidth: '6rem',
          maxWidth: '9rem',
          backgroundColor: active
            ? 'rgba(240, 165, 0, 0.18)'
            : 'var(--bg-elevated)',
          border: active
            ? '1.5px solid var(--accent-amber)'
            : '1px solid var(--border-default)',
          color: active ? 'var(--accent-amber)' : 'var(--text-secondary)',
        }}
      >
        {active && (
          <span
            className="absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full text-[9px] font-bold"
            style={{
              width: '1.1rem',
              height: '1.1rem',
              backgroundColor: waiting ? 'var(--accent-amber)' : '#3ECFCF',
              color: '#0E1420',
            }}
          >
            {place.token_count}
          </span>
        )}
        <span className="truncate text-center leading-tight">{place.label}</span>
      </div>
      {active && place.token_state && (
        <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
          {place.token_state}
        </span>
      )}
    </div>
  );
}

function Arrow() {
  return (
    <svg width="20" height="16" viewBox="0 0 20 16" className="flex-shrink-0 self-center">
      <line x1="0" y1="8" x2="16" y2="8" stroke="var(--border-strong)" strokeWidth="1.5" />
      <polyline points="11,4 16,8 11,12" fill="none" stroke="var(--border-strong)" strokeWidth="1.5" />
    </svg>
  );
}

function WorkflowCard({ wf }: { wf: WorkflowData }) {
  const active = wf.total_tokens > 0;
  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        border: active ? '1px solid var(--border-default)' : '1px solid var(--border-subtle)',
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Circle
            className="w-2.5 h-2.5 flex-shrink-0"
            fill={active ? 'var(--accent-amber)' : 'var(--text-tertiary)'}
            stroke="none"
          />
          <span
            className="text-sm font-semibold truncate"
            style={{ color: active ? 'var(--text-primary)' : 'var(--text-secondary)' }}
          >
            {wf.name}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {active && (
            <span
              className="px-2 py-0.5 rounded text-[10px] font-semibold"
              style={{
                backgroundColor: 'rgba(240, 165, 0, 0.15)',
                color: 'var(--accent-amber)',
              }}
            >
              {wf.total_tokens} token{wf.total_tokens !== 1 ? 's' : ''}
            </span>
          )}
          <span
            className="text-[10px] flex items-center gap-1"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <Clock className="w-3 h-3" />
            {formatAge(wf.last_activity_seconds)}
          </span>
        </div>
      </div>

      <div className="flex items-start gap-0 overflow-x-auto pb-1 scrollbar-thin">
        {wf.places.map((p, i) => (
          <React.Fragment key={p.id}>
            <PlaceChip place={p} />
            {i < wf.places.length - 1 && <Arrow />}
          </React.Fragment>
        ))}
      </div>

      <div className="text-[10px] font-mono truncate" style={{ color: 'var(--text-tertiary)' }}>
        {wf.id}
      </div>
    </div>
  );
}

export const ProcessObsDashboard: React.FC = () => {
  const [workflows, setWorkflows] = useState<WorkflowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/api/process-obs/workflows`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: WorkflowData[] = await res.json();
      setWorkflows(data);
      setLastRefresh(new Date());
      setError('');
    } catch (err: any) {
      setError(err.message ?? 'Failed to load process state');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  const activeCount = workflows.filter(w => w.total_tokens > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitBranch className="w-6 h-6" style={{ color: 'var(--accent-teal)' }} />
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Process Observatory
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Real-time Petri net token positions
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchData}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--hover-overlay)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm"
          style={{
            backgroundColor: 'rgba(239,68,68,0.12)',
            border: '1px solid rgba(239,68,68,0.3)',
            color: '#FCA5A5',
          }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-lg px-4 py-3 text-center"
          style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
        >
          <div className="text-2xl font-bold" style={{ color: 'var(--accent-teal)' }}>
            {workflows.length}
          </div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            Workflows
          </div>
        </div>
        <div
          className="rounded-lg px-4 py-3 text-center"
          style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
        >
          <div className="text-2xl font-bold" style={{ color: 'var(--accent-amber)' }}>
            {activeCount}
          </div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            Active
          </div>
        </div>
      </div>

      {loading && workflows.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
          Loading process state…
        </div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--text-tertiary)' }}>
          No workflows found in petri_net_state.db
        </div>
      ) : (
        <div className="space-y-3">
          {activeCount > 0 && (
            <h2
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Active ({activeCount})
            </h2>
          )}
          {workflows
            .filter(w => w.total_tokens > 0)
            .map(w => <WorkflowCard key={w.id} wf={w} />)}
          {workflows.filter(w => w.total_tokens === 0).length > 0 && (
            <>
              <h2
                className="text-xs font-semibold uppercase tracking-wider mt-4"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Idle ({workflows.filter(w => w.total_tokens === 0).length})
              </h2>
              {workflows
                .filter(w => w.total_tokens === 0)
                .map(w => <WorkflowCard key={w.id} wf={w} />)}
            </>
          )}
        </div>
      )}
    </div>
  );
};
